/*
 * Copyright (c) 2022 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Wednesday, April 13th 2022, 6:00:27 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import React from "react";
import { useToggleState } from "ui-widgets/dialogs/useToggleState";
import { InstallationConfirmationComponent } from "./InstallationConfirmationComponent";
import { InstallationForm, InstallationFormProps } from "./InstallationForm";

interface InstallationButtonWrapperProps
  extends Pick<
    InstallationFormProps,
    "installationFile" | "prebuiltServiceId" | "isAlreadyInstalled" | "serviceTitle" | "serviceCode"
  > {
  children: (options: { handleInstall: () => void }) => React.ReactNode;
}

export const InstallationButtonWrapper = (props: InstallationButtonWrapperProps) => {
  const {
    handleInstall,
    handleConfirmInstallation,
    closeInstallationFormFn,
    installationFormOpenState,
    installationConfirmationOpenState,
    closeInstallationConfirmation,
  } = usePreBuiltInstaller();

  const { children, installationFile, isAlreadyInstalled, prebuiltServiceId, serviceTitle, serviceCode } = props;

  return (
    <>
      {children({ handleInstall })}

      <InstallationConfirmationComponent
        installationConfirmationOpenState={installationConfirmationOpenState}
        closeInstallationConfirmation={closeInstallationConfirmation}
        handleConfirmInstallation={handleConfirmInstallation}
        isAlreadyInstalled={isAlreadyInstalled}
        serviceTitle={serviceTitle}
      />

      <InstallationForm
        prebuiltServiceId={prebuiltServiceId}
        isAlreadyInstalled={isAlreadyInstalled}
        serviceTitle={serviceTitle}
        serviceCode={serviceCode}
        installationFile={installationFile}
        closeInstallationFormFn={closeInstallationFormFn}
        installationFormOpenState={installationFormOpenState}
      />
    </>
  );
};

const usePreBuiltInstaller = () => {
  const {
    handleClose: closeInstallationConfirmation,
    open: installationConfirmationOpenState,
    handleOpen: openInstallationConfirmation,
  } = useToggleState();

  const {
    handleClose: closeInstallationFormFn,
    open: installationFormOpenState,
    handleOpen: openInstallationFormFn,
  } = useToggleState();

  const handleInstall = () => {
    openInstallationConfirmation();
  };

  const handleConfirmInstallation = () => {
    openInstallationFormFn();
  };

  return {
    handleInstall,
    handleConfirmInstallation,
    closeInstallationFormFn,
    installationFormOpenState,
    installationConfirmationOpenState,
    closeInstallationConfirmation,
  };
};
